import { Injectable, inject, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError, BehaviorSubject, Subject, of } from 'rxjs';
import { catchError, tap, takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HttpHeaderKeys } from '../constants/http-headers';
import { AuthService } from './auth.service';
import { trimObjectStrings } from '../utils/trim-object.util';

export interface ApiResponse<T = any> {
    data: T;
    message?: string;
    status: number;
    success: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class HttpService {
    private http = inject(HttpClient);
    private injector = inject(Injector);
    private router = inject(Router);
    private readonly baseUrl: string = environment.apiUrl;

    private _loading$ = new BehaviorSubject<boolean>(false);
    public loading$ = this._loading$.asObservable();

    private cancelSubjects = new Map<string, Subject<void>>();

    // Lazy getter to avoid circular dependency: HttpService → AuthService → PatientService → HttpService
    private get authService(): AuthService {
        return this.injector.get(AuthService);
    }

    private getFullUrl(urlPath: string, apiEndpoint: string = this.baseUrl): string {
        if (urlPath.startsWith('http://') || urlPath.startsWith('https://')) {
            return urlPath;
        }
        return `${apiEndpoint}${urlPath.startsWith('/') ? '' : '/'}${urlPath}`;
    }

    private getRequestKey(method: string, url: string, data?: any): string {
        const dataStr = data ? JSON.stringify(data) : '';
        return `${method}:${url}:${dataStr}`;
    }

    private cancelPreviousRequest(requestKey: string): void {
        const existing = this.cancelSubjects.get(requestKey);
        if (existing) {
            existing.next();
            existing.complete();
        }
        this.cancelSubjects.set(requestKey, new Subject<void>());
    }

    private setHeaders(
        useAuthenticationToken: boolean = true,
        useSessionToken: boolean = true,
        modulecode?: string | null,
        customHeaders?: Record<string, string> | null
    ): HttpHeaders {
        let headers: Record<string, string> = {
            [HttpHeaderKeys.HTTP_HEADER_CONTENT_TYPE]: HttpHeaderKeys.MIME_TYPE_JSON,
        };

        if (useAuthenticationToken) {
            const authToken = localStorage.getItem('auth_token');
            if (authToken) {
                headers[HttpHeaderKeys.HTTP_HEADER_AUTHORIZATION] = authToken;
            }
        }

        if (useSessionToken) {
            const sessionToken = localStorage.getItem('token');
            if (sessionToken) {
                headers[HttpHeaderKeys.HTTP_HEADER_TOKEN] = sessionToken;
            }
        }

        if (modulecode) {
            headers[HttpHeaderKeys.HTTP_HEADER_MODULE_CODE] = modulecode;
        }

        if (customHeaders) {
            headers = { ...headers, ...customHeaders };
        }

        return new HttpHeaders(headers);
    }

    private updateSessionToken(response: any): void {
        if (response?.token) {
            localStorage.setItem('token', response.token);
        }
    }

    private trimRequestBody(data: any): any {
        if (!data) return data;
        if (data instanceof FormData || data instanceof Blob) return data;
        if (typeof data === 'object') return trimObjectStrings(data);
        return data;
    }

    private handleError(error: any): Observable<never> {
        if (error?.name === 'CanceledError') {
            return throwError(() => error);
        }

        if (error?.status === 401 || error?.code === 'NotAuthorizedException') {
            this.authService.clearCredentials();
            this.router.navigate(['/login']);
            return throwError(() => new Error('Your connection has expired for security reasons. Please sign in again.'));
        }

        let errorMessage = 'An error occurred';
        if (error instanceof HttpErrorResponse) {
            if (error.error instanceof ErrorEvent) {
                errorMessage = error.error.message;
            } else if (error.status === 0) {
                errorMessage = 'Unable to connect to the server. Please check your network connection.';
            } else {
                errorMessage = error.error?.message || error.error?.value || `Error Code: ${error.status}`;
            }
        } else if (error?.message) {
            errorMessage = error.message;
        }

        if (environment.showLogs) {
            console.error('HTTP Error:', errorMessage, error);
        }

        return throwError(() => new Error(errorMessage));
    }

    private setLoading(loading: boolean): void {
        this._loading$.next(loading);
    }

    get<T = any>(
        urlPath: string,
        httpParams?: any,
        modulecode?: string | null,
        allowMultipleRequest: boolean = false,
        useAuthenticationToken: boolean = true,
        useSessionToken: boolean = true,
        apiEndpoint: string = this.baseUrl,
        customHeaders: Record<string, string> | null = null
    ): Observable<T> {
        this.setLoading(true);
        const requestKey = this.getRequestKey('GET', urlPath, httpParams);

        if (!allowMultipleRequest) {
            this.cancelPreviousRequest(requestKey);
        }

        const cancelSubject = this.cancelSubjects.get(requestKey) || new Subject<void>();
        const url = this.getFullUrl(urlPath, apiEndpoint);
        const headers = this.setHeaders(useAuthenticationToken, useSessionToken, modulecode, customHeaders);
        const params = httpParams ? new HttpParams({ fromObject: httpParams }) : undefined;

        return this.http.get<T>(url, { headers, params }).pipe(
            takeUntil(cancelSubject),
            tap(res => { this.updateSessionToken(res); this.setLoading(false); }),
            catchError(err => { this.setLoading(false); return this.handleError(err); })
        ) as Observable<T>;
    }

    post<T = any>(
        urlPath: string,
        data?: any,
        modulecode?: string | null,
        allowMultipleRequest: boolean = false,
        useAuthenticationToken: boolean = true,
        useSessionToken: boolean = true,
        apiEndpoint: string = this.baseUrl,
        customHeaders: Record<string, string> | null = null
    ): Observable<T> {
        this.setLoading(true);
        const requestKey = this.getRequestKey('POST', urlPath, data);

        if (!allowMultipleRequest) {
            this.cancelPreviousRequest(requestKey);
        }

        const cancelSubject = this.cancelSubjects.get(requestKey) || new Subject<void>();
        const url = this.getFullUrl(urlPath, apiEndpoint);
        const trimmedData = this.trimRequestBody(data);
        const headers = this.setHeaders(useAuthenticationToken, useSessionToken, modulecode, customHeaders);

        return this.http.post<T>(url, trimmedData ?? {}, { headers }).pipe(
            takeUntil(cancelSubject),
            tap(res => { this.updateSessionToken(res); this.setLoading(false); }),
            catchError(err => { this.setLoading(false); return this.handleError(err); })
        ) as Observable<T>;
    }

    put<T = any>(
        urlPath: string,
        data?: any,
        modulecode?: string | null,
        allowMultipleRequest: boolean = false,
        useAuthenticationToken: boolean = true,
        useSessionToken: boolean = true,
        apiEndpoint: string = this.baseUrl,
        customHeaders: Record<string, string> | null = null
    ): Observable<T> {
        this.setLoading(true);
        const requestKey = this.getRequestKey('PUT', urlPath, data);

        if (!allowMultipleRequest) {
            this.cancelPreviousRequest(requestKey);
        }

        const cancelSubject = this.cancelSubjects.get(requestKey) || new Subject<void>();
        const url = this.getFullUrl(urlPath, apiEndpoint);
        const trimmedData = this.trimRequestBody(data);
        const headers = this.setHeaders(useAuthenticationToken, useSessionToken, modulecode, customHeaders);

        return this.http.put<T>(url, trimmedData ?? {}, { headers }).pipe(
            takeUntil(cancelSubject),
            tap(res => { this.updateSessionToken(res); this.setLoading(false); }),
            catchError(err => { this.setLoading(false); return this.handleError(err); })
        ) as Observable<T>;
    }

    patch<T = any>(
        urlPath: string,
        data?: any,
        modulecode?: string | null,
        allowMultipleRequest: boolean = false,
        useAuthenticationToken: boolean = true,
        useSessionToken: boolean = true,
        apiEndpoint: string = this.baseUrl,
        customHeaders: Record<string, string> | null = null
    ): Observable<T> {
        this.setLoading(true);
        const requestKey = this.getRequestKey('PATCH', urlPath, data);

        if (!allowMultipleRequest) {
            this.cancelPreviousRequest(requestKey);
        }

        const cancelSubject = this.cancelSubjects.get(requestKey) || new Subject<void>();
        const url = this.getFullUrl(urlPath, apiEndpoint);
        const trimmedData = this.trimRequestBody(data);
        const headers = this.setHeaders(useAuthenticationToken, useSessionToken, modulecode, customHeaders);

        return this.http.patch<T>(url, trimmedData ?? {}, { headers }).pipe(
            takeUntil(cancelSubject),
            tap(res => { this.updateSessionToken(res); this.setLoading(false); }),
            catchError(err => { this.setLoading(false); return this.handleError(err); })
        ) as Observable<T>;
    }

    delete<T = any>(
        urlPath: string,
        modulecode?: string | null,
        allowMultipleRequest: boolean = false,
        useAuthenticationToken: boolean = true,
        useSessionToken: boolean = true,
        apiEndpoint: string = this.baseUrl,
        customHeaders: Record<string, string> | null = null
    ): Observable<T> {
        this.setLoading(true);
        const requestKey = this.getRequestKey('DELETE', urlPath);

        if (!allowMultipleRequest) {
            this.cancelPreviousRequest(requestKey);
        }

        const cancelSubject = this.cancelSubjects.get(requestKey) || new Subject<void>();
        const url = this.getFullUrl(urlPath, apiEndpoint);
        const headers = this.setHeaders(useAuthenticationToken, useSessionToken, modulecode, customHeaders);

        return this.http.delete<T>(url, { headers }).pipe(
            takeUntil(cancelSubject),
            tap(res => { this.updateSessionToken(res); this.setLoading(false); }),
            catchError(err => { this.setLoading(false); return this.handleError(err); })
        ) as Observable<T>;
    }

    downloadImage(url: string): Observable<Blob | undefined> {
        this.setLoading(true);
        const headers = this.setHeaders(true, false);
        return this.http.get(url, { headers, responseType: 'blob' }).pipe(
            tap(() => this.setLoading(false)),
            catchError(() => { this.setLoading(false); return of(undefined); })
        );
    }

    cancelRequest(method: string, urlPath: string, data?: any): void {
        const requestKey = this.getRequestKey(method.toUpperCase(), urlPath, data);
        const subject = this.cancelSubjects.get(requestKey);
        if (subject) {
            subject.next();
            subject.complete();
            this.cancelSubjects.delete(requestKey);
        }
    }

    cancelAllRequests(): void {
        this.cancelSubjects.forEach(subject => {
            subject.next();
            subject.complete();
        });
        this.cancelSubjects.clear();
    }
}
